import React, { useEffect, useMemo, useState } from "react"
import { motion } from "framer-motion"
import { HiOutlineFlag, HiOutlineCheckCircle, HiOutlineXCircle, HiOutlineUserMinus } from "react-icons/hi2"
import { useHistory } from "react-router-dom"
import API from "../../Common/API"
import { useDispatch } from "react-redux"
import { setAlert } from "../../Store/Theme/actions"

const STATUS_FILTERS = [
    { value: "pending", label: "Pending" },
    { value: "resolved", label: "Resolved" },
    { value: "dismissed", label: "Dismissed" },
    { value: "all", label: "All" },
]

const formatName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(" ") || u?.refId || "Member"
const formatDate = (iso) => {
    if (!iso) return ""
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

const STATUS_PILL = {
    pending: "bg-[#fff8e6] text-[#a07414]",
    resolved: "bg-[#ecf8ef] text-[#1a8a3a]",
    dismissed: "bg-slate-100 text-slate-600",
}

const ReportRow = ({ report, onView, onResolve, onDismiss, onDeactivate }) => {
    const isPending = report.status === "pending"
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-borderColor bg-white p-5 shadow-sm"
        >
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] ${STATUS_PILL[report.status] || STATUS_PILL.pending}`}>
                            {report.status}
                        </span>
                        <span className="rounded-full border border-[#efcaca] bg-[#fff5f5] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-[#a03e3e]">
                            {report.reason}
                        </span>
                        <span className="text-xs text-tpColor">{formatDate(report.createdAt)}</span>
                    </div>

                    <div className="mt-3 grid gap-3 text-sm md:grid-cols-2">
                        <div className="rounded-xl bg-lightGrey/40 p-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-tpColor">Reporter</p>
                            <p className="mt-1 font-semibold text-tColor">{formatName(report.reporter)}</p>
                            <p className="text-xs text-tpColor">{report.reporter?.refId} · {report.reporter?.mobileNumber}</p>
                        </div>
                        <div className="rounded-xl bg-[#fff5f5] p-3">
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#a03e3e]">Reported</p>
                            <p className="mt-1 font-semibold text-tColor">{formatName(report.reportedUser)}</p>
                            <p className="text-xs text-tpColor">{report.reportedUser?.refId} · {report.reportedUser?.mobileNumber}</p>
                        </div>
                    </div>

                    {report.details && (
                        <div className="mt-3 rounded-xl border border-borderColor bg-white p-3 text-sm leading-6 text-tColor">
                            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-tpColor">Details</p>
                            <p className="mt-1 whitespace-pre-wrap">{report.details}</p>
                        </div>
                    )}

                    {!isPending && (
                        <p className="mt-3 text-xs text-tpColor">
                            {report.status === "resolved" ? `Resolved at ${formatDate(report.resolvedAt)}` : `Dismissed at ${formatDate(report.resolvedAt)}`}
                            {report.actionTaken && <> · Action: <strong>{report.actionTaken}</strong></>}
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap gap-2">
                    {report.reportedUser?._id && (
                        <button
                            type="button"
                            onClick={() => onView(report)}
                            className="inline-flex items-center gap-1 rounded-full border border-borderColor bg-white px-3 py-1.5 text-xs font-bold text-tColor hover:border-primeColor hover:text-primeColor"
                        >
                            View profile
                        </button>
                    )}
                    {isPending && (
                        <>
                            <button
                                type="button"
                                onClick={() => onDeactivate(report)}
                                className="inline-flex items-center gap-1 rounded-full border border-[#efcaca] bg-[#fff5f5] px-3 py-1.5 text-xs font-bold text-[#a03e3e] hover:border-[#a03e3e]"
                            >
                                <HiOutlineUserMinus className="h-4 w-4" />
                                Resolve & Deactivate
                            </button>
                            <button
                                type="button"
                                onClick={() => onResolve(report)}
                                className="inline-flex items-center gap-1 rounded-full border border-[#cfe8d4] bg-[#f2fbf4] px-3 py-1.5 text-xs font-bold text-[#1a8a3a] hover:border-[#1a8a3a]"
                            >
                                <HiOutlineCheckCircle className="h-4 w-4" />
                                Resolve
                            </button>
                            <button
                                type="button"
                                onClick={() => onDismiss(report)}
                                className="inline-flex items-center gap-1 rounded-full border border-borderColor bg-white px-3 py-1.5 text-xs font-bold text-tpColor hover:border-tColor hover:text-tColor"
                            >
                                <HiOutlineXCircle className="h-4 w-4" />
                                Dismiss
                            </button>
                        </>
                    )}
                </div>
            </div>
        </motion.div>
    )
}

const Reports = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const [reports, setReports] = useState([])
    const [statusFilter, setStatusFilter] = useState("pending")
    const [loading, setLoading] = useState(false)

    const load = async () => {
        setLoading(true)
        try {
            const params = statusFilter === "all" ? {} : { status: statusFilter }
            const res = await API.get("/reports/list", { params })
            setReports(res.data?.data || [])
        } catch (err) {
            dispatch(setAlert({ title: "Error", subtitle: err?.response?.data?.error || "Could not load reports", type: "error", active: true }))
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { load() }, [statusFilter]) // eslint-disable-line

    const counts = useMemo(() => {
        const acc = { pending: 0, resolved: 0, dismissed: 0 }
        reports.forEach((r) => { acc[r.status] = (acc[r.status] || 0) + 1 })
        return acc
    }, [reports])

    const act = async (path, body) => {
        try {
            await API.post(path, body || {})
            await load()
        } catch (err) {
            dispatch(setAlert({ title: "Error", subtitle: err?.response?.data?.error || "Action failed", type: "error", active: true }))
        }
    }

    return (
        <motion.div className="space-y-5">
            <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Moderation</p>
                <h2 className="text-2xl font-semibold text-tColor flex items-center gap-2">
                    <HiOutlineFlag className="h-6 w-6" />
                    Reports
                </h2>
                <p className="text-sm text-tpColor">Review user-submitted reports. "Resolve & Deactivate" disables the reported member's account.</p>
            </div>

            <div className="inline-flex flex-wrap rounded-full border border-borderColor bg-white p-1 text-sm font-bold">
                {STATUS_FILTERS.map((f) => (
                    <button
                        key={f.value}
                        type="button"
                        onClick={() => setStatusFilter(f.value)}
                        className={`rounded-full px-4 py-2 transition ${statusFilter === f.value ? "bg-primeColor text-white" : "text-tColor"}`}
                    >
                        {f.label}{f.value !== "all" ? ` (${counts[f.value] || 0})` : ""}
                    </button>
                ))}
            </div>

            {loading && <div className="rounded-2xl border border-borderColor bg-white p-6 text-sm text-tpColor">Loading...</div>}
            {!loading && reports.length === 0 && (
                <div className="rounded-2xl border border-dashed border-borderColor bg-lightGrey/30 p-6 text-sm text-tpColor">
                    No reports in this view.
                </div>
            )}

            <div className="grid gap-4">
                {reports.map((r) => (
                    <ReportRow
                        key={r._id}
                        report={r}
                        onView={(rep) => history.push(`/view/${rep.reportedUser._id}`)}
                        onResolve={(rep) => act(`/reports/${rep._id}/resolve`, { actionTaken: "warn" })}
                        onDismiss={(rep) => act(`/reports/${rep._id}/dismiss`)}
                        onDeactivate={(rep) => act(`/reports/${rep._id}/resolve`, { actionTaken: "deactivate" })}
                    />
                ))}
            </div>
        </motion.div>
    )
}

export default Reports
